"use client"
import { Image as KonvaImage } from "react-konva";
import React, { useState, useEffect, useRef } from "react";
import { Stage, Layer, Line, Circle } from "react-konva";
import Konva from "konva";
import {
  Flex,
  Button,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  ModalCloseButton,
  Image as ChakraImage,
  Box,
} from "@chakra-ui/react";
import { IoMdClose } from "react-icons/io";
import { BsEraser } from "react-icons/bs";
import { createIcon } from "@chakra-ui/react";

interface MaskingToolProps {
  onClose: (maskedImage: string | null, maskBase64: string | null) => void;
  image: string;
  mask: string | null;
  
}


interface LineType {
  tool: "brush" | "eraser";
  strokeWidth: number;
  points: number[];
}

interface StageDimensions {
  width: number;
  height: number;
}

interface CursorPosition {
  x: number;
  y: number;
}

const ResetIcon = createIcon({
  displayName: "ResetIcon",
  viewBox: "0 0 32 32",
  path: (
    <path
      fill="currentColor"
      d="M7 27h23v2H7zm20.38-16.49l-7.93-7.92a2 2 0 0 0-2.83 0l-14 14a2 2 0 0 0 0 2.83L7.13 24h9.59l10.66-10.66a2 2 0 0 0 0-2.83zM15.89 22H8l-4-4l6.31-6.31l7.93 7.92zm3.76-3.76l-7.92-7.93L18 4l8 7.93z"
    />
  ),
});

const BrushIcon = createIcon({
  displayName: "BrushIcon",
  viewBox: "0 0 32 32",
  path: [
    <path
      d="M28.828 3.172a4.094 4.094 0 0 0-5.656 0L4.05 22.292A6.954 6.954 0 0 0 2 27.242V30h2.756a6.952 6.952 0 0 0 4.95-2.05L28.828 8.829a3.999 3.999 0 0 0 0-5.657z"
      fill="currentColor"
    />,
    <path
      d="M6.5 15a3.5 3.5 0 0 1-2.475-5.974l3.5-3.5a1.502 1.502 0 0 0 0-2.121a1.537 1.537 0 0 0-2.121 0L3.415 5.394L2 3.98l1.99-1.988a3.585 3.585 0 0 1 4.95 0a3.504 3.504 0 0 1 0 4.949L5.439 10.44a1.502 1.502 0 0 0 0 2.121a1.537 1.537 0 0 0 2.122 0l4.024-4.024L13 9.95l-4.025 4.024A3.475 3.475 0 0 1 6.5 15z"
      fill="currentColor"
    />,
  ],
});

const UndoIcon = createIcon({
  displayName: "UndoIcon",
  viewBox: "0 0 24 24",
  path: [
    <polyline
      points="1 4 1 10 7 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
    <path
      d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  ],
});

const MaskingTool: React.FC<MaskingToolProps> = ({ image, mask, onClose }) => {
  const [stageDimensions, setStageDimensions] = useState<StageDimensions>({ width: 0, height: 0 });
  const [scale, setScale] = useState<number>(1);
  const [lines, setLines] = useState<LineType[]>([]);
  const [brushSize, setBrushSize] = useState<number>(20);
  const [tool, setTool] = useState<"brush" | "eraser">("brush");
  const [isCursorInStage, setIsCursorInStage] = useState<boolean>(false);
  const [cursorPosition, setCursorPosition] = useState<CursorPosition>({ x: 0, y: 0 });
  const isDrawing = useRef<boolean>(false);
  const stageRef = useRef<Konva.Stage | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [maskImageElement, setMaskImageElement] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new window.Image();
    img.src = image;
    img.onload = () => {
      const maxWidth = window.innerWidth * 0.8;
      const maxHeight = window.innerHeight * 0.7;
      
      const imageRatio = img.naturalWidth / img.naturalHeight;
      const containerRatio = maxWidth / maxHeight;
      
      let newWidth, newHeight;
      
      if (imageRatio > containerRatio) {
        newWidth = maxWidth;
        newHeight = maxWidth / imageRatio;
      } else {
        newHeight = maxHeight;
        newWidth = maxHeight * imageRatio;
      }

      setStageDimensions({
        width: Math.floor(newWidth),
        height: Math.floor(newHeight)
      });
      setScale(newWidth / img.naturalWidth);
    };
 // If a mask is provided, load and render it
 if (mask) {
  const maskImg = new window.Image();
  maskImg.src = mask; // Mask is the provided base64
  maskImg.onload = () => {
    setMaskImageElement(maskImg);
  };
}
}, [image, mask]);



  const handleCursorDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void => {
    isDrawing.current = true;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    setLines([...lines, { tool, strokeWidth: brushSize, points: [pos.x, pos.y] }]);
  };

  const handleCursorMove = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void => {
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    if (!isDrawing.current) {
      setCursorPosition({ x: pos.x, y: pos.y });
      return;
    }
    
    const lastLine = lines[lines.length - 1];
    if (lastLine) {
      lastLine.points = lastLine.points.concat([pos.x, pos.y]);
      lines.splice(lines.length - 1, 1, lastLine);
      setLines([...lines]);
    }
  };


  const handleConfirm = () => {
    if (!stageRef.current) {
      onClose(null, null);
      return;
    }
  
    // Create a canvas for the mask
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = stageDimensions.width;
    maskCanvas.height = stageDimensions.height;
    const ctx = maskCanvas.getContext("2d");
  
    if (ctx) {
      // Draw the current mask from the Konva Stage
      const dataURL = stageRef.current.toDataURL();
      const maskImage = new Image();
      maskImage.src = dataURL;
  
      maskImage.onload = () => {
        // Draw the mask onto the mask canvas
        ctx.drawImage(maskImage, 0, 0, stageDimensions.width, stageDimensions.height);
  
        // Create a new canvas to resize the mask to the original image dimensions
        const finalMaskCanvas = document.createElement("canvas");
        const img = new Image();
        img.src = image;
  
        img.onload = () => {
          // Set the final mask canvas dimensions to the original image dimensions
          finalMaskCanvas.width = img.naturalWidth;
          finalMaskCanvas.height = img.naturalHeight;
  
          const finalCtx = finalMaskCanvas.getContext("2d");
  
          if (finalCtx) {
            // Scale the mask to match the original image dimensions
            finalCtx.drawImage(
              maskCanvas,
              0,
              0,
              stageDimensions.width,
              stageDimensions.height,
              0,
              0,
              img.naturalWidth,
              img.naturalHeight
            );
  
            // Convert the final resized mask to base64
            const finalMaskBase64 = finalMaskCanvas.toDataURL();
  
            // Return the final resized mask to the parent component
            onClose(null, finalMaskBase64);
          }
        };
      };
    }
  };
  
  
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w="100%"
      h="100%"
      gap="4"
      bg="#0A0E1D"
      p="6"
    >
      <Text
        bg="linear-gradient(to right, #FFF0E1 34%, #A4DBEE 100%)"
        bgClip="text"
        color="transparent"
        fontSize={{ base: "48px", lg: "72px" }}
        fontWeight="600"
      >
        Select Inpaint
      </Text>

      <Text
        color="white"
        fontSize={{ base: "14px", lg: "16px" }}
        mb="8"
      >
        Select area for generation
      </Text>

      {/* Tools */}
      <Flex gap="4" mb="4" maxW="800px" w="full" justify="center">
        <Button
          _hover={{ opacity: 0.9, bg: "gray.600" }}
          minH="50px"
          bg="transparent"
          color="white"
          fontSize={{ base: "14px", lg: "18px" }}
          border="1px solid white"
          borderRadius="0"
          onClick={() => { setLines([]);
            setMaskImageElement(null); 
          }}
          leftIcon={<Icon as={ResetIcon} boxSize={{ base: 3, lg: 4 }} />}
        >
          Reset
        </Button>

        <Button
          _hover={{ opacity: 0.9, bg: "gray.600" }}
          minH="50px"
          bg="transparent"
          color="white"
          fontSize={{ base: "14px", lg: "18px" }}
          border="1px solid white"
          borderRadius="0"
          onClick={() => setLines(prev => prev.slice(0, -1))}
          leftIcon={<Icon as={BrushIcon} boxSize={{ base: 3, lg: 4 }} />}
        >
          Undo
        </Button>

        <Slider
          aria-label="Brush size"
          min={1}
          max={50}
          value={brushSize}
          onChange={(val) => setBrushSize(val)}
          w="200px"
        >
          <SliderTrack bg="white">
            <SliderFilledTrack bg="#FF69B4" />
          </SliderTrack>
          <SliderThumb />
        </Slider>

        <Menu>
          <MenuButton
            as={Button}
            _hover={{ opacity: 0.9, bg: "gray.600" }}
            minH="50px"
            bg="transparent"
            color="white"
            fontSize={{ base: "14px", lg: "18px" }}
            border="1px solid white"
            borderRadius="0"
            leftIcon={tool === "eraser" ? <BsEraser /> : <Icon as={BrushIcon} />}
          >
            {tool === "eraser" ? "Eraser" : "Brush"}
          </MenuButton>
          <MenuList bg="#0A0E1D" border="1px solid white">
            <MenuItem bg="#0A0E1D" _hover={{ bg: "#1B1F2E" }} onClick={() => setTool("brush")}>
              Brush
            </MenuItem>
            <MenuItem bg="#0A0E1D" _hover={{ bg: "#1B1F2E" }} onClick={() => setTool("eraser")}>
              Eraser
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      {/* Canvas */}
      <Box
        ref={containerRef}
        position="relative"
        w={`${stageDimensions.width}px`}
        h={`${stageDimensions.height}px`}
      >
        <ChakraImage
          src={image}
          alt="Canvas"
          w="100%"
          h="100%"
          objectFit="contain"
          userSelect="none"
          draggable={false}
        />
        
        <Box
          position="absolute"
          top="0"
          left="0"
          w="100%"
          h="100%"
          opacity={0.5}
        >
          <Stage
            width={stageDimensions.width}
            height={stageDimensions.height}
            onMouseEnter={() => setIsCursorInStage(true)}
            onMouseLeave={() => setIsCursorInStage(false)}
            onMouseDown={handleCursorDown}
            onMouseMove={handleCursorMove}
            onMouseUp={() => isDrawing.current = false}
            onTouchStart={handleCursorDown}
            onTouchMove={handleCursorMove}
            onTouchEnd={() => isDrawing.current = false}
            ref={stageRef}
          >
            <Layer>
                {/* Render the existing mask */}
                {maskImageElement && (
                <KonvaImage
                  image={maskImageElement}
                  width={stageDimensions.width}
                  height={stageDimensions.height}
                />
              )}
              {lines.map((line, i) => (
                line.points.length === 2 ? (
                  <Circle
                    key={i}
                    x={line.points[0]}
                    y={line.points[1]}
                    radius={line.strokeWidth / 2}
                    fill="white"
                    globalCompositeOperation={line.tool === "eraser" ? "destination-out" : "source-over"}
                  />
                ) : (
                  <Line
                    key={i}
                    points={line.points}
                    stroke="white"
                    strokeWidth={line.strokeWidth}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={line.tool === "eraser" ? "destination-out" : "source-over"}
                  />
                )
              ))}
            </Layer>
          </Stage>

          {isCursorInStage && (
            <Box
              position="absolute"
              style={{
                backgroundColor: "white",
                opacity: 0.5,
                height: `${brushSize}px`,
                width: `${brushSize}px`,
                borderRadius: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                left: `${cursorPosition.x}px`,
                top: `${cursorPosition.y}px`,
              }}
            >
              <Box
                style={{
                  backgroundColor: "gray",
                  height: "15%",
                  width: "15%",
                  minHeight: "1px",
                  minWidth: "1px",
                  borderRadius: "50%",
                  marginLeft: "50%",
                  marginTop: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Button
  bg="#FF1B6D"
  color="white"
  onClick={handleConfirm}  
  w="200px"
  minH="50px"
  mt="4"
  fontSize={{ base: "16px", "2xl": "20px" }}
  _hover={{ bg: "#d01357" }}
>
  Confirm
</Button>

      <ModalCloseButton 
        position="absolute"
        top="4"
        right="4"
        color="white"
        _hover={{ bg: "gray.700" }}
      />
    </Flex>
  );
};

export default MaskingTool;